import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { BasePage, LinkButton } from "components";
import { Grid, Box, Alert, Typography, Button } from "@mui/material";
import { CheckCircle, Clear } from "@mui/icons-material";
import { useAuthentication } from "hooks";
import { EMAIL_VERIFICATION_ERROR_MESSAGES, getErrorMessage } from "constants";

const VERIFY_TIMEOUT_MS = 15000;

export function VerifyEmailPage() {
    const { verifyEmailWithCode, loading } = useAuthentication();
    const [searchParams] = useSearchParams();
    
    const oobCode =
        searchParams.get("oobCode") ||
        new URLSearchParams(window.location.search).get("oobCode") ||
        "";
    const mode =
        searchParams.get("mode") ||
        new URLSearchParams(window.location.search).get("mode") ||
        "";

    const [phase, setPhase] = useState("init"); // init | verifying | success | error
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");

    const startedRef = useRef(false);
    const mountedRef = useRef(false);

    const stripParams = useCallback(() => {
        const url = new URL(window.location.href);
        ["mode", "oobCode", "apiKey", "lang", "continueUrl"].forEach((p) => url.searchParams.delete(p));
        url.searchParams.set("done", "1");
        window.history.replaceState({}, "", url.toString());
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const runVerify = useCallback(async () => {
        setPhase("verifying");
        setError("");
        let to = setTimeout(() => {
            if (!mountedRef.current) return;
            setPhase("error");
            setError("Network timeout while verifying. Please try again.");
        }, VERIFY_TIMEOUT_MS);

        try {
            const res = await verifyEmailWithCode(oobCode);
            if (!mountedRef.current) return;       
            setEmail(res?.email || "");
            setPhase("success");
            stripParams();
        } catch (err) {
            if (!mountedRef.current) return;
            
            setPhase("error");
            const errorMessage = getErrorMessage(err, EMAIL_VERIFICATION_ERROR_MESSAGES);
            setError(errorMessage);
        } finally {
            clearTimeout(to);
        }
    }, [oobCode, verifyEmailWithCode, stripParams]);


    useEffect(() => {
        if (loading) return; 
        
        if (!oobCode) {
            setPhase("error");
            setError("Invalid verification link (no code).");
            return;
        }
        
        if (mode && mode !== "verifyEmail") {
            setPhase("error");
            setError("This link is not an email verification link.");
            return;
        }
        
        if (startedRef.current) return;
        startedRef.current = true;
        
        runVerify();
    }, [loading, oobCode, mode, runVerify]);

    if (phase === "init") {
        return (
            <BasePage title="Verify email">
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography>Initializing…</Typography>
                </Box>
            </BasePage>
        );
    }

    return (
        <BasePage title="Verify email">
            <Grid container spacing={2}>
                {phase === "verifying" && (
                    <Grid size={12}>
                        <Box sx={{ textAlign: "center", py: 3 }}>
                            <Typography>Verifying your email…</Typography>
                        </Box>
                    </Grid>
                )}

                {phase === "success" && (
                    <>
                        <Grid size={12}>
                            <Alert icon={<CheckCircle fontSize="inherit" />} severity="success">
                                {email ? `Email ${email} verified. You can now continue.` : "Your email is verified. You can now continue."}
                            </Alert>
                        </Grid>
                        <Grid size={12} sx={{ textAlign: "center" }}>
                            <LinkButton to="/sign-in">Go to sign in</LinkButton>
                        </Grid>
                    </>
                )}

                {phase === "error" && (
                    <>
                        <Grid size={12}>
                            <Alert icon={<Clear fontSize="inherit" />} severity="error">
                                {error}
                            </Alert>
                        </Grid>
                        <Grid size={12} sx={{ textAlign: "center", gap: 1, display: "flex", justifyContent: "center" }}>
                            <Button variant="outlined" onClick={runVerify}>Try again</Button>
                            <LinkButton to="/sign-in">Back to sign in</LinkButton>
                        </Grid>
                    </>
                )}
            </Grid>
        </BasePage>
    );
}